/**
 * Settings panel: set the operator org id.
 *
 * The org id is NON-secret — it's the tenant identifier every unauthenticated
 * surface (the cleanup reaper, the CLI, background jobs) resolves to. Persisted
 * to a scoped HashiCorp Vault path; the change hot-reloads server-side, so it
 * takes effect immediately without a restart. The `MAESTRO_OPERATOR_ORG_ID`
 * env value remains the cold-boot default.
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { getOperatorOrgStatus, updateOperatorOrg } from '@/lib/api';

const QUERY_KEY = ['operatorOrgStatus'] as const;

export function OperatorOrgPanel(): React.ReactElement {
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    data: status,
    isLoading,
    isError,
  } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getOperatorOrgStatus,
  });

  // Seed the field with the current effective org once status first loads.
  useEffect(() => {
    if (status && value === '') setValue(status.orgId);
  }, [status, value]);

  const saveMutation = useMutation({
    mutationFn: (orgId: string) => updateOperatorOrg({ orgId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setError(null);
      setSuccess(true);
    },
    onError: (err: Error) => {
      setError(err.message);
      setSuccess(false);
    },
  });

  const trimmed = value.trim();
  const vaultConfigured = status?.vaultConfigured ?? false;
  const unchanged = trimmed === (status?.orgId ?? '');
  const canSave = vaultConfigured && trimmed.length > 0 && !unchanged && !saveMutation.isPending;

  const handleSave = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSave) return;
    setSuccess(false);
    saveMutation.mutate(trimmed);
  };

  return (
    <Card className="rounded-2xl border-border/70 bg-surface/95 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-3">
          <span>Operator Org</span>
          {!isLoading && status && (
            <Badge variant={vaultConfigured ? 'secondary' : 'outline'}>
              {vaultConfigured ? 'Vault-backed' : 'env only'}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-4 text-sm text-text-secondary">
          The org every unauthenticated surface resolves to — the cleanup reaper, the CLI, and
          background jobs. Stored in a scoped HashiCorp Vault path and applied immediately, no
          restart. <code>MAESTRO_OPERATOR_ORG_ID</code> is the cold-boot default.
        </p>

        {isError && (
          <p className="mb-4 text-xs text-destructive">
            Couldn&apos;t load operator-org status from the server.
          </p>
        )}

        <form onSubmit={handleSave} className="space-y-4">
          <div className="space-y-1">
            <label htmlFor="operator-org-id" className="text-xs font-medium text-text-primary">
              Operator org id
            </label>
            <Input
              id="operator-org-id"
              value={value}
              onChange={e => {
                setValue(e.target.value);
                setSuccess(false);
              }}
              placeholder="org-aaron@mahoosuc.solutions"
              autoComplete="off"
              spellCheck={false}
              disabled={!vaultConfigured}
              className="font-mono text-sm"
            />
            {status && (
              <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px] text-text-tertiary">
                <dt>In effect now</dt>
                <dd className="font-mono">{status.orgId}</dd>
                <dt>In Vault</dt>
                <dd className="font-mono">{status.vaultValue ?? '— (not set)'}</dd>
                <dt>Env default</dt>
                <dd className="font-mono">{status.envDefault ?? '— (none)'}</dd>
              </dl>
            )}
            {!vaultConfigured && status && (
              <p className="text-[11px] text-destructive">
                Vault AppRole not configured — set MAESTRO_VAULT_ADDR / MAESTRO_VAULT_ROLE_ID /
                MAESTRO_VAULT_SECRET_ID to enable saving.
              </p>
            )}
            {status?.vaultStatus === 'unreachable' && (
              <p className="text-[11px] text-destructive">
                Vault is configured but unreachable — the saved value can&apos;t be read (check
                MAESTRO_VAULT_ADDR / mesh). Background jobs are running on the env/default org.
              </p>
            )}
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}
          {success && (
            <p className="text-xs text-success dark:text-success">Saved — applied immediately.</p>
          )}

          <Button type="submit" disabled={!canSave} className="gap-2">
            <Save className="h-4 w-4" />
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
